import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { downloadAttachment } from "@/lib/files";
import { InlineSpinner } from "@/components/common/Loading";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { BookletFlip } from "@/components/BookletFlip";
import { toast } from "sonner";
import {
  GraduationCap, Store, Handshake, UserPlus, Briefcase, CheckCircle2, Clock, HelpCircle, ArrowRight,
  FileText, Building2, TrendingUp, Users, ShieldCheck, Award, DollarSign, MapPin, Rocket, Sparkles, MessageSquare, Download, BadgeCheck,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function monthYear(d) {
  if (!d) return "";
  const dt = new Date(d);
  return isNaN(dt) ? "" : dt.toLocaleDateString("en-US", { month: "long", year: "numeric" });
}

function BrochureActions({ pg }) {
  const b = pg.brochure;
  if (!b?.available) return null;
  const dl = async () => {
    try { await downloadAttachment(`${BACKEND}/api${b.download_path.replace(/^\/api/, "")}`, b.filename); }
    catch { toast.error("Could not download the brochure"); }
  };
  return (
    <div className="flex flex-wrap items-center gap-3" data-testid="program-brochure-actions">
      <Button variant="outline" onClick={dl} data-testid="program-download-brochure"
        className="border-[#006090]/40 text-[#006090] hover:bg-[#006090]/5">
        <Download className="w-4 h-4 mr-2" />Download Brochure
      </Button>
      {b.reviewed && b.current_as_of && (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-3 py-1 text-xs font-medium" data-testid="program-reviewed-badge">
          <BadgeCheck className="w-3.5 h-3.5" />Reviewed &amp; Current · Updated {monthYear(b.current_as_of)}
        </span>
      )}
    </div>
  );
}

export const PROGRAM_ICONS = {
  Store, Handshake, UserPlus, GraduationCap, Briefcase, Building2, TrendingUp, Users, ShieldCheck,
  Award, DollarSign, MapPin, Rocket, Sparkles,
};

const Section = ({ title, subtitle, children, testid }) => (
  <section className="mb-10" data-testid={testid || `program-section-${(title || "").toLowerCase().replace(/[^a-z]+/g, "-")}`}>
    {title && <h2 className="font-display text-2xl font-bold text-slate-900">{title}</h2>}
    {subtitle && <p className="text-sm text-slate-500 mt-1 mb-5 max-w-2xl">{subtitle}</p>}
    {!subtitle && title && <div className="mb-5" />}
    {children}
  </section>
);

const DEFAULT_DISCLAIMER = "Program details, incentives, requirements and figures are informational only, are subject to change, and follow current program guidelines. Nothing on this page is a guarantee of compensation, eligibility, or appointment.";

/** Reusable marketing/brochure program page. Shared by internal sidebar + Candidate Portal. */
export function ProgramPageView({ pg, isMine = false, onCta, onNavigate }) {
  const Icon = PROGRAM_ICONS[pg.icon] || GraduationCap;
  const cta = (action) => { if (onCta) onCta(action, pg); };
  const capitalLink = () => {
    const slug = pg.capital_link_slug || "capital-requirements";
    if (onNavigate) onNavigate(slug); else window.location.assign(`/programs/${slug}`);
  };
  const asOf = (pg.effective_date || pg.updated_at || "").slice(0, 10);

  return (
    <div className="max-w-5xl" data-testid={`program-page-${pg.slug}`}>
      {/* HERO */}
      <div className="rounded-3xl bg-gradient-to-br from-[#013A5A] to-[#025b86] text-white p-8 sm:p-12 mb-10 overflow-hidden relative">
        {pg.hero_image && <img src={pg.hero_image} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />}
        <div className="absolute inset-0 bg-gradient-to-t from-[#013A5A]/80 to-transparent" />
        <div className="relative max-w-2xl">
          <div className="flex items-center gap-3 mb-4">
            <span className="w-11 h-11 rounded-xl bg-white/15 flex items-center justify-center"><Icon className="w-6 h-6" /></span>
            {pg.badge_label && <Badge className="bg-[#0D9488] text-white border-0" data-testid="program-badge">{pg.badge_label}</Badge>}
            {isMine && <Badge className="bg-white text-[#013A5A] border-0" data-testid="your-program-badge">Your Program</Badge>}
            {pg.active === false && <Badge variant="outline" className="border-amber-300 text-amber-200" data-testid="program-inactive-badge">Inactive</Badge>}
          </div>
          <p className="text-brand-cyan text-xs font-semibold uppercase tracking-wide">{pg.title}</p>
          <h1 className="font-display text-3xl sm:text-5xl font-bold leading-tight mt-1">{pg.hero_headline || pg.title}</h1>
          {(pg.hero_subheadline || pg.value_prop || pg.overview) && (
            <p className="text-white/85 text-base mt-4">{pg.hero_subheadline || pg.value_prop || pg.overview}</p>
          )}
          <div className="flex flex-wrap gap-3 mt-7">
            {pg.cta_primary_label && (
              <Button onClick={() => cta(pg.cta_primary_action)} data-testid="program-cta-primary" className="bg-[#0D9488] hover:bg-[#0b7d73] text-white border-0">
                <MessageSquare className="w-4 h-4 mr-2" />{pg.cta_primary_label}
              </Button>
            )}
            {pg.cta_secondary_label && (
              <Button variant="outline" onClick={() => cta(pg.cta_secondary_action)} data-testid="program-cta-secondary" className="bg-white/10 border-white/40 text-white hover:bg-white/20">
                {pg.cta_secondary_label}<ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* AT A GLANCE */}
      {pg.at_a_glance?.length > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10" data-testid="program-at-a-glance">
          {pg.at_a_glance.map((s, i) => (
            <div key={i} className="rounded-2xl border border-slate-200 bg-white p-5">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{s.label}</p>
              <p className="mt-1 font-display text-lg font-bold text-slate-900 leading-tight">{s.value}</p>
            </div>
          ))}
        </div>
      )}

      {pg.overview && pg.hero_subheadline && <Section testid="program-section-overview"><p className="text-base text-slate-600 leading-relaxed">{pg.overview}</p></Section>}

      {/* PROGRAM BOOKLET — flip book */}
      {pg.booklet_images?.length > 0 ? (
        <Section title="Program Booklet" subtitle="Flip through the official program overview." testid="program-section-booklet">
          <BookletFlip images={pg.booklet_images} />
          <div className="mt-4"><BrochureActions pg={pg} /></div>
        </Section>
      ) : pg.brochure?.available && (
        <Section title="Program Brochure" subtitle="Download the official program overview (PDF)." testid="program-section-brochure">
          <BrochureActions pg={pg} />
        </Section>
      )}

      {/* WHY CHOOSE */}
      {pg.why_choose?.length > 0 && (
        <Section title={`Why choose ${pg.title}?`} testid="program-section-why">
          <div className="grid sm:grid-cols-2 gap-4">
            {pg.why_choose.map((w, i) => {
              const WI = PROGRAM_ICONS[w.icon] || Sparkles;
              return (
                <div key={i} className="rounded-2xl border border-slate-200 bg-white p-5 flex gap-4">
                  <span className="w-11 h-11 rounded-xl bg-[#006090]/10 text-[#006090] flex items-center justify-center shrink-0"><WI className="w-5 h-5" /></span>
                  <div><p className="font-semibold text-slate-900">{w.title}</p><p className="text-sm text-slate-600 mt-0.5">{w.text}</p></div>
                </div>
              );
            })}
          </div>
        </Section>
      )}

      {/* HOW IT WORKS — JOURNEY */}
      {pg.journey?.length > 0 ? (
        <Section title="How it works" subtitle="Your journey, step by step." testid="program-section-how">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {pg.journey.map((j, i) => (
              <div key={i} className="relative rounded-2xl border border-slate-200 bg-white p-5">
                <span className="absolute -top-3 -left-2 w-8 h-8 rounded-full bg-[#0D9488] text-white text-sm font-bold flex items-center justify-center">{i + 1}</span>
                <p className="font-semibold text-slate-900 mt-1">{j.label}</p>
                <p className="text-sm text-slate-600 mt-1">{j.description}</p>
              </div>
            ))}
          </div>
        </Section>
      ) : pg.how_it_works && <Section title="How it works" testid="program-section-how"><p className="text-base text-slate-600 leading-relaxed">{pg.how_it_works}</p></Section>}

      {/* INCENTIVES */}
      {pg.incentives?.length > 0 && (
        <Section title="Incentives & opportunities" testid="program-section-incentives">
          <div className="grid sm:grid-cols-2 gap-4">
            {pg.incentives.map((inc, i) => (
              <div key={i} className="rounded-2xl border border-[#0D9488]/30 bg-[#0D9488]/5 p-5">
                <p className="font-semibold text-slate-900 flex items-center gap-2"><Award className="w-4 h-4 text-[#0D9488]" />{inc.title}</p>
                <p className="text-sm text-slate-600 mt-1">{inc.text}</p>
                {inc.qualifier && <p className="text-xs text-slate-400 italic mt-2">{inc.qualifier}</p>}
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* FINANCIAL ILLUSTRATIONS */}
      {pg.illustrations?.length > 0 && (
        <Section title="Illustrations" subtitle="Examples of potential opportunity — not guaranteed compensation." testid="program-section-illustrations">
          <div className="grid sm:grid-cols-2 gap-4">
            {pg.illustrations.map((il, i) => (
              <div key={i} className="rounded-2xl border border-slate-200 bg-white p-5">
                <div className="flex items-center justify-between"><p className="font-semibold text-slate-900">{il.title}</p><Badge variant="outline" className="text-[10px] border-amber-300 text-amber-600">Illustration</Badge></div>
                {il.caption && <p className="text-sm text-slate-600 mt-1">{il.caption}</p>}
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* RELATED INFORMATION LINKS */}
      {pg.related_links?.length > 0 && (
        <Section title="Related Information" testid="program-section-related-links">
          <div className="flex flex-wrap gap-3">
            {pg.related_links.map((l, i) => (
              <button key={i} onClick={() => { if (onNavigate) onNavigate(l.slug); else window.location.assign(`/programs/${l.slug}`); }} data-testid={`related-link-${i}`} className="inline-flex items-center gap-1.5 rounded-full border border-[#006090]/30 bg-[#006090]/5 px-4 py-2 text-sm font-medium text-[#006090] hover:bg-[#006090]/10">
                <FileText className="w-4 h-4" />{l.label}<ArrowRight className="w-4 h-4" />
              </button>
            ))}
          </div>
        </Section>
      )}

      {/* CAPITAL REQUIREMENT */}
      {pg.capital_note && (
        <Section title="Capital Requirement" testid="program-section-capital">
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5">
            <p className="text-sm text-slate-700">{pg.capital_note}</p>
            {pg.capital_link_label && (
              <button onClick={capitalLink} data-testid="capital-learn-more" className="mt-3 inline-flex items-center gap-1.5 text-[#006090] font-semibold text-sm hover:underline">
                {pg.capital_link_label}<ArrowRight className="w-4 h-4" />
              </button>
            )}
          </div>
        </Section>
      )}

      {/* CONTENT (PDF converted to text) */}
      {pg.content_text && (
        <Section title="Details" testid="program-section-content">
          <div className="text-sm text-slate-600 leading-relaxed whitespace-pre-line">{pg.content_text}</div>
        </Section>
      )}

      {/* WHAT YOU'LL NEED — high-level only */}
      {pg.what_youll_need?.length > 0 && (
        <Section title="Preparing for the program" subtitle="A high-level summary — your personal checklist lives on your candidate record." testid="program-section-prepare">
          <div className="grid sm:grid-cols-2 gap-2.5">
            {pg.what_youll_need.map((s, i) => (
              <div key={i} className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-3">
                <CheckCircle2 className="w-4 h-4 text-[#0D9488] shrink-0" /><span className="text-sm text-slate-700">{s}</span>
              </div>
            ))}
          </div>
        </Section>
      )}

      {pg.training && <Section title="Training" testid="program-section-training"><p className="text-base text-slate-600">{pg.training}</p></Section>}      {pg.timeline && <Section title="Typical timeline" testid="program-section-timeline"><span className="inline-flex items-center gap-2 text-slate-600"><Clock className="w-4 h-4 text-slate-400" />{pg.timeline}</span></Section>}

      {/* COMPARISON */}
      {pg.comparison?.length > 0 && (
        <Section title="Is this program right for you?" testid="program-section-comparison">
          <div className="grid sm:grid-cols-2 gap-4">
            {pg.comparison.map((c, i) => (
              <div key={i} className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                <p className="font-semibold text-slate-900">{c.program}</p>
                <p className="text-sm text-slate-600 mt-1">{c.text}</p>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* FAQs */}
      {pg.faqs?.length > 0 && (
        <Section title="Frequently asked questions" testid="program-section-faqs">
          <div className="space-y-3">
            {pg.faqs.map((f, i) => (
              <div key={i} className="rounded-2xl border border-slate-200 bg-white p-4">
                <p className="font-medium text-slate-800 flex items-center gap-1.5"><HelpCircle className="w-4 h-4 text-slate-400" />{f.q}</p>
                <p className="text-slate-600 mt-1 pl-5 text-sm">{f.a}</p>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* RESOURCES */}
      {pg.resources?.length > 0 && (
        <Section title="Approved resources" testid="program-section-resources">
          <ul className="space-y-1.5">
            {pg.resources.map((r, i) => (
              <li key={i}><a href={r.url} target="_blank" rel="noopener noreferrer" className="text-[#006090] hover:underline inline-flex items-center gap-1.5"><FileText className="w-4 h-4" />{r.label || r.url}</a></li>
            ))}
          </ul>
        </Section>
      )}

      {/* CLOSING CTA */}
      {pg.cta_primary_label && (
        <div className="rounded-3xl bg-[#013A5A] text-white p-8 text-center" data-testid="program-closing-cta">
          <h3 className="font-display text-2xl font-bold">Ready to take the next step?</h3>
          <p className="text-white/80 text-sm mt-2">Your recruiter can answer questions and help you get started.</p>
          <Button onClick={() => cta(pg.cta_primary_action)} className="mt-5 bg-[#0D9488] hover:bg-[#0b7d73] border-0"><MessageSquare className="w-4 h-4 mr-2" />{pg.cta_primary_label}</Button>
        </div>
      )}

      <div className="border-t border-slate-100 pt-4 mt-8 text-xs text-slate-400 space-y-1.5" data-testid="program-disclaimer">
        {asOf && <p data-testid="program-asof">Information current as of {asOf}.</p>}
        <p>{pg.disclaimer || DEFAULT_DISCLAIMER}</p>
        <p>This is a program-information page. Your personal progress is tracked privately on your candidate record under Milestones.</p>
      </div>
    </div>
  );
}

export const RECRUITER_CTA_ACTIONS = new Set(["message_recruiter", "talk_recruiter", "contact_recruiter"]);

export default function ProgramPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [pg, setPg] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/programs/pages/${slug}`).then((r) => setPg(r.data)).catch((e) => { toast.error(apiError(e)); setPg(false); }).finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="py-20 flex justify-center"><InlineSpinner /></div>;
  if (!pg) return <div className="py-20 text-center text-slate-500" data-testid="program-not-found">This program page is not available.</div>;
  return <ProgramPageView pg={pg} isMine={false} onNavigate={(s) => navigate(`/programs/${s}`)}
    onCta={(action) => {
      if (RECRUITER_CTA_ACTIONS.has(action)) navigate("/my-team#my-recruiter");
    }} />;
}
